import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getSearchSuggestions } from "../services/soundcloud/api";

const SUGGESTION_DEBOUNCE_MS = 240;

function normalizeSuggestions(payload) {
  const collection = Array.isArray(payload?.collection) ? payload.collection : [];
  const values = collection
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        return String(item.query || item.q || item.term || "").trim();
      }
      return "";
    })
    .filter(Boolean);

  return [...new Set(values)].slice(0, 6);
}

function TopBar({
  title,
  subtitle,
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  searchDisabled = false,
  hideSearch = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const canSuggest = useMemo(
    () => !searchDisabled && String(searchValue || "").trim().length >= 2,
    [searchDisabled, searchValue]
  );

  useEffect(() => {
    if (!canSuggest) {
      setSuggestions([]);
      setIsSuggestionLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setIsSuggestionLoading(true);
      try {
        const payload = await getSearchSuggestions(searchValue, { limit: 6 });
        if (!cancelled) {
          setSuggestions(normalizeSuggestions(payload));
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setIsSuggestionLoading(false);
      }
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [canSuggest, searchValue]);

  function submitSearch(rawValue) {
    const normalized = String(rawValue || "").trim();
    onSearchSubmit?.(normalized);
    setShowSuggestions(false);
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    submitSearch(searchValue);
  }

  return (
    <header className="topbar glass-panel">
      <div>
        <p className="view-subtitle">{subtitle}</p>
        <h1 className="view-title">{title}</h1>
      </div>

      <div className="topbar-actions">
        {!hideSearch && (
          <form className="search-wrap" onSubmit={handleSearchSubmit} autoComplete="off">
            <label className="search-box" htmlFor="search-music">
              <Search size={16} />
              <input
                id="search-music"
                name="search_music_query"
                placeholder={
                  searchDisabled
                    ? "Search is available in Home tab"
                    : "Search songs, artists, playlists..."
                }
                value={searchValue}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  window.setTimeout(() => setShowSuggestions(false), 120);
                }}
                onChange={(event) => onSearchChange?.(event.target.value)}
                disabled={searchDisabled}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
              />
            </label>
            <button type="submit" className="icon-btn search-submit-btn" aria-label="Search">
              <Search size={16} />
            </button>
            {showSuggestions && !searchDisabled && (suggestions.length > 0 || isSuggestionLoading) ? (
              <div className="search-suggestions glass-panel">
                {isSuggestionLoading ? (
                  <p className="suggestion-empty">Loading suggestions...</p>
                ) : (
                  suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="suggestion-item"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        onSearchChange?.(suggestion);
                        submitSearch(suggestion);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </form>
        )}
      </div>
    </header>
  );
}

export default TopBar;
